// v14.1 — Vocal Recorder
//
// Wraps a MediaRecorder attached to the mic (`getUserMedia({audio}`) and
// gives the rest of the app a very small API:
//
//   const rec = new VocalRecorder();
//   await rec.start();          // asks for mic permission, opens stream
//   ... user records for N seconds ...
//   const { wavBlob, buffer, peaks } = await rec.stop();
//   rec.dispose();
//
// The producer's mic stays hot for the LIFETIME of a single VocalRecorder
// instance so successive record takes don't re-prompt for permission —
// call `dispose()` when the DAW no longer needs the mic (page unmount,
// or the user un-arms every track).
//
// Peaks are generated inline at ~200 peaks/second so the recorded clip
// paints its waveform in the arranger immediately, no async decode.

import audioBufferToWav from "audiobuffer-to-wav";

export class VocalRecorder {
  constructor() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.mimeType = "";
    this.recording = false;
  }

  async open() {
    if (this.stream) return;
    // Ask for the mic. The stream stays open for repeat takes.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    });
    // Prefer opus (broadest browser support + small); fall back to
    // whatever MediaRecorder is willing to give us.
    const candidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "",
    ];
    this.mimeType = candidates.find((m) => !m || (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m))) || "";
  }

  async start() {
    await this.open();
    if (this.recording) return;
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream, this.mimeType ? { mimeType: this.mimeType } : undefined);
    this.recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) this.chunks.push(e.data); };
    this.recorder.start(100); // gather chunks every 100ms
    this.recording = true;
  }

  async stop() {
    if (!this.recorder || !this.recording) return null;
    this.recording = false;
    const doneP = new Promise((res) => { this.recorder.onstop = () => res(); });
    this.recorder.stop();
    await doneP;
    if (this.chunks.length === 0) return null;
    const rawBlob = new Blob(this.chunks, { type: this.mimeType || "audio/webm" });
    // Decode the recording to a raw AudioBuffer so we can (a) re-encode
    // as WAV (universal DAW format, matches Bounce/Export) and (b)
    // pre-compute peaks for the waveform preview.
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuf = await rawBlob.arrayBuffer();
    const buffer = await ctx.decodeAudioData(arrayBuf.slice(0));
    try { ctx.close(); } catch { /* noop */ }
    const wavArr = audioBufferToWav(buffer);
    const wavBlob = new Blob([wavArr], { type: "audio/wav" });
    // v19.27 — Dense min/max pair peaks for the continuous waveform
    // renderer. Shared helper keeps recordings and file drops on the
    // exact same format.
    const { buildPeakPairs } = await import("./decodeAudio");
    const peaks = buildPeakPairs(buffer);
    return { wavBlob, buffer, peaks, peaksFormat: "pairs", duration: buffer.duration };
  }

  dispose() {
    try {
      if (this.recorder && this.recording) this.recorder.stop();
    } catch { /* noop */ }
    if (this.stream) {
      for (const t of this.stream.getTracks()) {
        try { t.stop(); } catch { /* noop */ }
      }
    }
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.recording = false;
  }
}

// App-wide singleton — one mic pipe shared across the DAW.
export const vocalRecorder = new VocalRecorder();
